import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Mail } from "lucide-react";
import { testSendBrief } from "@/lib/api";
import { toast } from "sonner";

export default function TestSendModal({ open, onOpenChange, brief, onSent }) {
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState(false);
  if (!brief) return null;

  const valid = /\S+@\S+\.\S+/.test(recipient);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await testSendBrief(brief.id, recipient);
      const status = res?.delivery?.status;
      if (status === "sent" || status === "delivered") {
        toast.success("Test email sent.", { description: `Look in ${recipient} for a [TEST]-labeled brief.` });
      } else if (res?.delivery?.failureReason?.includes("EMAIL_SEND_ENABLED")) {
        toast("Test recorded — sending is disabled.", {
          description: "Enable EMAIL_SEND_ENABLED and set RESEND_API_KEY to actually deliver test emails.",
        });
      } else {
        toast.error("Test send failed.", { description: res?.delivery?.failureReason || "See admin alerts." });
      }
      onSent?.(res?.delivery);
      onOpenChange(false);
      setRecipient("");
    } catch (err) {
      toast.error("Test send failed.", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-[24px]" data-testid="test-send-modal">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0"
              style={{ background: "#FFF3D6", color: "#8A5A00" }}>
              <Mail size={20} />
            </div>
            <div>
              <DialogTitle className="font-display text-xl font-semibold" style={{ color: "var(--ta-primary)" }}>
                Send a test email
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
                Test sends are clearly labeled and never change the brief status. A separate audit record is created.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <label className="text-sm font-medium mt-2 inline-block" style={{ color: "var(--ta-primary)" }}>
          Test recipient email
        </label>
        <input
          type="email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && valid && submit()}
          placeholder="you@yourdomain.com"
          data-testid="test-send-recipient"
          className="w-full rounded-xl bg-white border-2 px-4 py-3 text-base outline-none focus:border-[var(--ta-primary)]"
          style={{ borderColor: "var(--ta-border)", color: "var(--ta-text)" }}
          autoFocus
        />

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <button onClick={() => onOpenChange(false)} data-testid="test-send-cancel"
            className="flex-1 min-h-[48px] rounded-xl text-sm font-medium border-2"
            style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}>
            Never mind
          </button>
          <button onClick={submit} disabled={!valid || busy} data-testid="test-send-submit"
            className="flex-1 min-h-[48px] rounded-xl text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: "var(--ta-primary)" }}>
            {busy ? <Loader2 size={18} className="animate-spin inline" /> : "Send test"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
